/**
 * usePlanAuthorizationRight — may the signed-in person authorize this plan,
 * as far as the cycle's decision rights go? (D-4 phase 2, mig 843.)
 *
 * A plan cycle is governed by the prioritization cycle its input came from.
 * Which cycle that is, is the database's answer (`plan_cycle_governing_cycle`,
 * the same join `authorize_por` uses), so the app and the act cannot disagree.
 * The right itself is evaluated by `useDecisionRightGate`: authorizing a plan
 * is the "items / selection" right, exercised solo. With no governing cycle,
 * or no such right set on it, this allows — today's behaviour.
 *
 * This decides what the page draws. `authorize_por` is the control.
 */

import { useEffect, useMemo, useState } from 'react';
import { fetchPlanGoverningCycleId } from '@/lib/planGoverningCycle';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useDecisionRightGate, type DecisionRightGate } from './useDecisionRightGate';
import { useActorDisplayMap } from './useActorDisplayMap';
import type { DecisionRight } from '@/types/prioritization/decisionRights';

export interface PlanAuthorizationRight extends DecisionRightGate {
  /** The prioritization cycle whose rights govern this plan, when there is one. */
  governingCycleId: string | null;
  /**
   * The plain line to show in place of the Authorize control when the person
   * may not use it (build contract §2.8): who holds the right, by name — or,
   * when they hold it themselves but not for deciding alone, why not. Null
   * when they may authorize.
   */
  refusalLine: string | null;
}

/** Who holds a decision right, by name. */
function rightHolderPhrase(right: DecisionRight, names: Record<string, string>): string {
  const person = right.owner.principalId ? names[right.owner.principalId] ?? 'the person who holds it' : null;
  switch (right.owner.bindingType) {
    case 'PRINCIPAL_IN_SCOPE':
      return person ?? 'the person who holds it';
    case 'ROLE_IN_SCOPE':
      return `anyone with the ${right.owner.roleId ?? 'assigned'} role`;
    case 'ROLE_AND_PRINCIPAL':
      return `${person ?? 'the person who holds it'} (as ${right.owner.roleId ?? 'the assigned role'})`;
  }
}

export function usePlanAuthorizationRight(planCycleId: string | null | undefined): PlanAuthorizationRight {
  const [governingCycleId, setGoverningCycleId] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const userId = useUserStore((s) => s.userId);
  const capabilityIds = useUserStore((s) => s.resolvedCapabilityIds);
  const roleNames = useUserStore((s) => s.assignedRoleNames);
  // A role binding names a role or a permission it carries; offer both, as the
  // database's internal_actor_holds_role_binding matches both.
  const userRoleIds = useMemo(() => [...capabilityIds, ...roleNames], [capabilityIds, roleNames]);

  useEffect(() => {
    setGoverningCycleId(null);
    if (!planCycleId) return;
    let cancelled = false;
    setResolving(true);
    void fetchPlanGoverningCycleId(planCycleId).then((id) => {
      if (cancelled) return;
      setGoverningCycleId(id);
      setResolving(false);
    });
    return () => {
      cancelled = true;
    };
  }, [planCycleId]);

  const gate = useDecisionRightGate({
    cycleId: governingCycleId,
    domain: 'items',
    decisionClass: 'selection',
    mode: 'solo',
    userRoleIds,
  });

  const holderIds = useMemo(
    () => (gate.right?.owner.principalId ? [gate.right.owner.principalId] : []),
    [gate.right],
  );
  const holderNames = useActorDisplayMap(holderIds);
  const refusalLine =
    gate.allowed || !gate.right
      ? null
      : gate.holder === 'delegate' || gate.right.owner.principalId === userId
        ? gate.reason ?? null
        : `Authorizing this plan belongs to ${rightHolderPhrase(gate.right, holderNames)}, under the decision rights of the cycle it was built from.`;

  return { ...gate, loading: gate.loading || resolving, governingCycleId, refusalLine };
}
